import React from 'react';
import {
  ChevronLeft, ChevronRight, CalendarRange, CalendarDays, Send, EyeOff, Loader2, Layers,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SERIF, SANS, IITH } from '@/pages/landing/iithBrand';

export default function RosterToolbar({
  track, period, setPeriodMode, rangeLabel, shiftPeriod,
  specialisations, specialisation, setSpecialisation,
  staffList, staffId, setStaffId,
  stats, onPublish, onUnpublish, onBulkAllocate, publishing, publishLabel,
}) {
  const accent = track.accent;
  const filtered = specialisation
    ? staffList.filter((d) => d.specialization === specialisation)
    : staffList;

  return (
    <div className="space-y-4" data-testid="roster-toolbar">
      <div
        className="rounded-2xl border overflow-hidden px-5 py-5 sm:px-6"
        style={{
          background: `linear-gradient(120deg, ${IITH.navy} 0%, ${IITH.navyMid} 55%, #123556 100%)`,
        }}
      >
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em]" style={{ color: IITH.gold }}>
              IITH Hospital · Duty planning
            </p>
            <h1 className="text-2xl sm:text-3xl font-semibold text-white mt-1" style={{ fontFamily: SERIF }}>
              {track.label} roster
            </h1>
            <p className="text-sm mt-1.5 max-w-xl" style={{ color: IITH.mute, fontFamily: SANS }}>
              {track.hero}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={onBulkAllocate}
              className="border-white/30 bg-white/10 text-white hover:bg-white/20" data-testid="roster-bulk-open">
              <Layers className="h-4 w-4 mr-1.5" /> Bulk allocate
            </Button>
            <Button variant="outline" onClick={onUnpublish}
              className="border-white/30 bg-white/10 text-white hover:bg-white/20" data-testid="roster-unpublish">
              <EyeOff className="h-4 w-4 mr-1.5" /> Unpublish
            </Button>
            <Button onClick={onPublish} disabled={publishing} style={{ background: accent }}
              className="text-white hover:opacity-90" data-testid="roster-publish">
              {publishing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
              {publishLabel}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { l: 'Shifts', v: stats.shifts },
          { l: 'Published', v: stats.published },
          { l: 'Draft', v: stats.draft },
          { l: `${track.staffNounPlural} rostered`, v: stats.staffOn },
        ].map((s) => (
          <div key={s.l} className="rounded-xl border bg-white px-4 py-3 shadow-sm"
            style={{ borderTopColor: accent, borderTopWidth: 3 }}>
            <p className="text-[11px] uppercase tracking-wide text-zinc-500">{s.l}</p>
            <p className="text-2xl font-semibold text-zinc-900 mt-0.5" style={{ fontFamily: SERIF }}>{s.v}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 bg-white border rounded-xl p-3">
        <div className="inline-flex rounded-lg border overflow-hidden">
          {['week', 'month'].map((mode) => (
            <button key={mode} type="button" onClick={() => setPeriodMode(mode)}
              className={`px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1 ${period === mode ? 'text-white' : 'text-zinc-600 bg-white'}`}
              style={period === mode ? { background: accent } : {}}
              data-testid={`roster-period-${mode}`}>
              {mode === 'week' ? <CalendarRange className="h-3.5 w-3.5" /> : <CalendarDays className="h-3.5 w-3.5" />}
              {mode === 'week' ? 'Week' : 'Month'}
            </button>
          ))}
        </div>
        <div className="inline-flex items-center gap-1 border rounded-lg px-1">
          <button type="button" className="p-1.5 hover:bg-zinc-50 rounded" onClick={() => shiftPeriod(-1)} aria-label="Previous">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-xs font-semibold text-zinc-800 px-2 min-w-[140px] text-center">{rangeLabel}</span>
          <button type="button" className="p-1.5 hover:bg-zinc-50 rounded" onClick={() => shiftPeriod(1)} aria-label="Next">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        {track.bookingAware && (
          <select className="h-9 border rounded-lg px-2 text-xs min-w-[160px]" value={specialisation}
            onChange={(e) => { setSpecialisation(e.target.value); setStaffId(''); }}
            data-testid="roster-filter-spec">
            <option value="">All specialisations</option>
            {specialisations.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <select className="h-9 border rounded-lg px-2 text-xs min-w-[180px]" value={staffId}
          onChange={(e) => setStaffId(e.target.value)} data-testid="roster-filter-staff">
          <option value="">All {track.staffNounPlural}</option>
          {filtered.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>
    </div>
  );
}
